// frontend/src/components/common/Header.js (Updated with search)
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, LogOut, Globe, Menu, X, Search } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import SearchBar from "../search/SearchBar";
import SearchModal from "../search/SearchModal";

const Header = () => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const { language, changeLanguage, t } = useLanguage();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src="/images/burnair-rounded.png"
              alt="Burnair"
              className="h-8 w-auto"
            />
            <span className="ml-3 text-xl font-semibold text-[#002855] hidden sm:block">
              burnair Academy
            </span>
            <span className="ml-3 text-lg font-semibold text-[#002855] sm:hidden">
              Academy
            </span>
          </Link>

          {/* Desktop Search - Center */}
          <div className="hidden md:block flex-1 max-w-lg mx-8">
            <SearchBar />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4 flex-shrink-0">
            <div className="flex items-center space-x-2">
              <Globe className="w-4 h-4 text-gray-500" />
              <select
                value={language}
                onChange={(e) => changeLanguage(e.target.value)}
                className="text-sm border-none bg-transparent focus:outline-none text-[#002855]"
              >
                <option value="en">EN</option>
                <option value="de">DE</option>
              </select>
            </div>

            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4 text-[#00A3E0]" />
                  <span className="text-sm text-[#002855]">
                    {user?.given_name}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-1 text-sm text-gray-600 hover:text-[#002855] transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>{t({ en: "Logout", de: "Abmelden" })}</span>
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="bg-[#002855] text-white px-4 py-2 rounded-md hover:bg-[#001a3d] transition-colors"
              >
                {t({ en: "Login", de: "Anmelden" })}
              </Link>
            )}
          </div>

          {/* Mobile buttons */}
          <div className="md:hidden flex items-center space-x-2">
            {/* Mobile Search Icon */}
            <button
              onClick={() => setShowSearchModal(true)}
              className="p-2 rounded-md text-[#002855] hover:text-[#00A3E0] transition-colors"
              aria-label={t({ en: "Search", de: "Suchen" })}
            >
              <Search className="w-6 h-6" />
            </button>

            {/* Mobile menu button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="p-2 rounded-md text-[#002855] hover:text-[#00A3E0] transition-colors"
            >
              {showMobileMenu ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {showMobileMenu && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Globe className="w-4 h-4 text-gray-500" />
                <select
                  value={language}
                  onChange={(e) => changeLanguage(e.target.value)}
                  className="text-sm border-none bg-transparent focus:outline-none text-[#002855]"
                >
                  <option value="en">EN</option>
                  <option value="de">DE</option>
                </select>
              </div>

              {isAuthenticated ? (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-[#00A3E0]" />
                    <span className="text-sm text-[#002855]">
                      {user?.given_name}
                    </span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-1 text-sm text-gray-600 hover:text-[#002855] transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>{t({ en: "Logout", de: "Abmelden" })}</span>
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setShowMobileMenu(false)}
                  className="block w-full bg-[#002855] text-white px-4 py-2 rounded-md hover:bg-[#001a3d] text-center transition-colors"
                >
                  {t({ en: "Login", de: "Anmelden" })}
                </Link>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Search Modal */}
      <SearchModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
      />
    </header>
  );
};

export default Header;
